import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./Modal.module.css";
import closeIcon from "../icons/close.svg";
import ModalV2 from "./ModalV2";

const Modal = ({
  open,
  setOpen,
  side,
  children,
  closeIconProps,
  className,
  draggable = false,
  onClose = () => {},
  version = "v2",
  closeOnOutsideClick=true
}) => {
  if (version === "v2")
    return (
      <ModalV2
        open={open}
        setOpen={setOpen}
        side={side}
        children={children}
        closeIconProps={closeIconProps}
        className={className}
        draggable={draggable}
        onClose={onClose}
        closeOnOutsideClick={closeOnOutsideClick}
      />
    );

  return (
    <ModalV1
      open={open}
      setOpen={setOpen}
      side={side}
      children={children}
      closeIconProps={closeIconProps}
      className={className}
      draggable={draggable}
      onClose={onClose}
      closeOnOutsideClick={closeOnOutsideClick}
    />
  );
};

const ModalV1 = ({
  open,
  setOpen,
  side,
  children,
  closeIconProps,
  className,
  draggable = false,
  onClose = () => {},
  closeOnOutsideClick=true
}) => {
  const modalRef = useRef(null);
  const dialogRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 }); // Current offset from initial position
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 }); // Mouse position when drag started

  const handleClose = useCallback(() => {
    setOpen(false);
    // Reset position when modal closes
    setPosition({ x: 0, y: 0 });
    onClose();
  }, [setOpen, onClose]);

  const handleDialogClick = (e) => {
    if (!closeOnOutsideClick) return;

    const modalNode = modalRef.current;
    if (modalNode?.contains(e.target)) return;
    handleClose();
  };

  // --- Dragging Handlers ---
  const handleMouseDown = (e) => {
    if (!draggable) return;

    if (e.target.closest(`.${styles.closeButton}`)) {
      return;
    }

    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });

    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;

      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (open) {
      dialogRef.current.showModal();
      if (draggable) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
      }
    } else {
      dialogRef.current?.close();
      if (draggable) {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      }
    }

    return () => {
      if (draggable) {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      }
    };
  }, [open, draggable, handleMouseMove, handleMouseUp]); // Added handleMouseUp to dependencies

  return (
    <dialog
      ref={dialogRef}
      onClick={handleDialogClick}
      className={[styles.ModalWrapper, className, side && styles[side]].join(
        " "
      )}
      style={
        draggable
          ? {
              transform: `translate(${position.x}px, ${position.y}px)`,
              // cursor: isDragging ? 'grabbing' : 'grab', // Change cursor for visual feedback
              cursor: draggable ? (isDragging ? "moving" : "move") : "auto",
            }
          : {}
      }
    >
      <div
        ref={modalRef}
        className={styles.Modal}
        onMouseDown={handleMouseDown}
      >
        <button
          onClick={handleClose}
          className={styles.closeButton}
          type="button"
        >
          <img
            src={closeIcon}
            alt="close"
            {...closeIconProps}
            className={[styles.closeIcon, closeIconProps?.className].join(" ")}
          />
        </button>
        <div className={styles.ModalContent}>{children}</div>
      </div>
    </dialog>
  );
};

export default Modal;
